import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";

import EcoPageHeader from "../../../components/eco-page-header/EcoPageHeader";
import EcoConfirmDialog from "../../../components/eco-ui/EcoConfirmDialog";
import { useGoBack } from "../../../hooks/useGoBack";
import type { RootState } from "../../../store/store";

import { listPayrollPeriodsApi, DpPayrollPeriodListItem } from "../api/payrollApi";
import {
  createProvisionSnapshotApi,
  DpProvisionHistory,
  DpProvisionOverview,
  DpProvisionRow,
  exportProvisionsApi,
  getProvisionHistoryApi,
  getProvisionsApi,
  todayIso,
  variationOf,
} from "../api/provisionsApi";
import { DpCostCenter, DpEmployer, listCostCentersApi, listEmployersApi } from "../api/settingsApi";
import { DP_EMPLOYEE_TAB_PAGE, DP_PAGE } from "../routes";
import { downloadBlob, formatCompetence, formatCpf, formatCurrency } from "../utils/format";
import "./Dp.css";
import "./DpEco.css";

type SortKey = "name" | "total";

const days = (value: number) => value.toLocaleString("pt-BR", { maximumFractionDigits: 2 });

/** "+ R$ 1.234,56 (+2,1%)" — variação contra a foto anterior. */
function Variation({ current, previous }: { current: string; previous: string | null }) {
  const v = variationOf(current, previous);
  if (!v) return <span className="dpMuted">—</span>;
  const sign = v.diff > 0 ? "+" : v.diff < 0 ? "−" : "";
  return (
    <span title={`Foto anterior: ${formatCurrency(previous)}`}>
      {sign} {formatCurrency(Math.abs(v.diff))}
      {v.pct !== null ? <span className="dpMuted"> ({sign}{Math.abs(v.pct).toLocaleString("pt-BR", { maximumFractionDigits: 1 })}%)</span> : null}
    </span>
  );
}

/** Visão geral das provisões de 13º, férias, 1/3 e FGTS de todos os colaboradores ativos. */
export default function ProvisionsPage() {
  const navigate = useNavigate();
  const goBack = useGoBack(DP_PAGE());
  const token = useSelector((state: RootState) => state.auth.accessToken);
  const [searchParams, setSearchParams] = useSearchParams();

  const asOf = searchParams.get("data") || todayIso();
  const employerParam = searchParams.get("empregador") || "";
  const costCenterId = searchParams.get("centro") || "";
  const storeId = searchParams.get("loja") || "";
  const setParam = useCallback(
    (key: string, value: string) => {
      const next = new URLSearchParams(searchParams);
      if (value) next.set(key, value);
      else next.delete(key);
      if (key === "centro") next.delete("loja");
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const [employers, setEmployers] = useState<DpEmployer[]>([]);
  const [costCenters, setCostCenters] = useState<DpCostCenter[]>([]);
  const [data, setData] = useState<DpProvisionOverview | null>(null);
  const [history, setHistory] = useState<DpProvisionHistory | null>(null);
  const [closedPeriods, setClosedPeriods] = useState<DpPayrollPeriodListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState<SortKey>("name");
  const [snapshot, setSnapshot] = useState<{ year: number; month: number } | null>(null);
  const [snapshotting, setSnapshotting] = useState(false);
  const [showHistory, setShowHistory] = useState(true);

  const activeEmployers = useMemo(() => employers.filter((e) => e.isActive), [employers]);
  const employerId = employerParam || activeEmployers[0]?.id || "";
  const stores = useMemo(() => {
    const scoped = costCenterId ? costCenters.filter((c) => c.id === costCenterId) : costCenters;
    const map = new Map<string, string>();
    for (const cc of scoped) for (const s of cc.stores) map.set(s.id, s.nomeFantasia);
    return Array.from(map.entries()).sort((a, b) => a[1].localeCompare(b[1], "pt-BR"));
  }, [costCenters, costCenterId]);

  useEffect(() => {
    if (!token) return;
    Promise.all([listEmployersApi(token), listCostCentersApi(token)])
      .then(([emp, cc]) => {
        setEmployers(emp);
        setCostCenters(cc);
      })
      .catch((error: any) => toast.error(error?.message || "Erro ao carregar os cadastros."));
  }, [token]);

  const load = useCallback(async () => {
    if (!token || !employerId) return;
    setLoading(true);
    try {
      setData(await getProvisionsApi(token, { asOf, employerId, costCenterId, storeId }));
    } catch (error: any) {
      toast.error(error?.message || "Erro ao carregar as provisões.");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [token, asOf, employerId, costCenterId, storeId]);

  const loadHistory = useCallback(async () => {
    if (!token || !employerId) return;
    try {
      const [h, periods] = await Promise.all([
        getProvisionHistoryApi(token, { employerId }),
        listPayrollPeriodsApi(token),
      ]);
      setHistory(h);
      setClosedPeriods(periods.filter((p) => p.kind === "MENSAL" && p.status === "FECHADO" && p.employerId === employerId));
    } catch (error: any) {
      toast.error(error?.message || "Erro ao carregar o histórico.");
    }
  }, [token, employerId]);

  useEffect(() => {
    load();
  }, [load]);
  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    const digits = q.replace(/\D/g, "");
    const filtered = (data?.rows ?? []).filter(
      (r) =>
        !q ||
        r.meta.fullName.toLowerCase().includes(q) ||
        (!!digits && (r.meta.cpf ?? "").includes(digits)) ||
        (r.meta.registrationNumber ?? "").toLowerCase() === q
    );
    return sort === "total" ? [...filtered].sort((a, b) => Number(b.totalAmount) - Number(a.totalAmount)) : filtered;
  }, [data, query, sort]);

  const exportCsv = async () => {
    if (!token) return;
    setExporting(true);
    try {
      const blob = await exportProvisionsApi(token, { asOf, employerId, costCenterId, storeId });
      downloadBlob(blob, `provisoes-${asOf}.csv`);
    } catch (error: any) {
      toast.error(error?.message || "Erro ao exportar.");
    } finally {
      setExporting(false);
    }
  };

  const generateSnapshot = async () => {
    if (!token || !snapshot || !employerId) return;
    setSnapshotting(true);
    try {
      const result = await createProvisionSnapshotApi(token, { employerId, ...snapshot });
      toast.success(`Foto de ${formatCompetence(result.year, result.month)} gerada para ${result.count} colaborador(es).`);
      for (const w of result.financeiroWarnings ?? []) toast.warning(w, { autoClose: false });
      setSnapshot(null);
      await Promise.all([load(), loadHistory()]);
    } catch (error: any) {
      toast.error(error?.message || "Erro ao gerar a foto.");
    } finally {
      setSnapshotting(false);
    }
  };

  const snapshotMonths = useMemo(() => new Set((history?.months ?? []).map((m) => `${m.year}-${m.month}`)), [history]);
  const closedWithoutSnapshot = closedPeriods.filter((p) => !snapshotMonths.has(`${p.year}-${p.month}`));
  const [snapshotChoice, setSnapshotChoice] = useState("");
  useEffect(() => {
    const first = closedPeriods[0];
    setSnapshotChoice(first ? `${first.year}-${first.month}` : "");
  }, [closedPeriods]);

  const openRow = (row: DpProvisionRow) => navigate(`${DP_EMPLOYEE_TAB_PAGE(row.employeeId, "provisoes")}&data=${asOf}`);

  if (!token) return null;

  const previous = data?.previous;
  const totals = data?.totals;

  return (
    <div className="dpLayout">
      <main className="dpMain">
        <div className="dpContainer">
          <EcoPageHeader
            crumbs={[{ label: "Departamento Pessoal", onClick: () => navigate(DP_PAGE()) }, { label: "Provisões" }]}
            title="Provisões"
            subtitle="13º, férias, 1/3 e FGTS que a empresa já deve a cada colaborador e ainda não pagou."
            onBack={goBack}
            backLabel="Voltar para o DP"
          />

          <div className="dpControls">
            <label className="dpFormGroup" style={{ margin: 0 }}>
              <span className="dpFormHint">Data de referência</span>
              <input className="dpInput" type="date" value={asOf} onChange={(e) => setParam("data", e.target.value)} />
            </label>
            {activeEmployers.length > 1 ? (
              <select className="dpSelect" aria-label="Empregador" value={employerId} onChange={(e) => setParam("empregador", e.target.value)}>
                {activeEmployers.map((e) => (
                  <option key={e.id} value={e.id}>
                    {e.tradeName || e.legalName}
                  </option>
                ))}
              </select>
            ) : null}
            <select className="dpSelect" aria-label="Centro de custo" value={costCenterId} onChange={(e) => setParam("centro", e.target.value)}>
              <option value="">Todos os centros de custo</option>
              {costCenters.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.code} · {c.name}
                </option>
              ))}
            </select>
            <select className="dpSelect" aria-label="Loja" value={storeId} onChange={(e) => setParam("loja", e.target.value)}>
              <option value="">Todas as lojas</option>
              {stores.map(([id, name]) => (
                <option key={id} value={id}>
                  {name}
                </option>
              ))}
            </select>
            <button type="button" className="dpBtn dpBtn--secondary" onClick={exportCsv} disabled={exporting || !data}>
              {exporting ? "Exportando..." : "Exportar CSV"}
            </button>
          </div>

          <p className="dpNotice">
            É conferência, como a folha: o que seria devido numa dispensa sem justa causa na data, sem aviso prévio nem multa do FGTS.
            O INSS da empresa não entra. A foto de cada mês é gravada ao fechar a folha mensal.
          </p>

          {activeEmployers.length === 0 && employers.length > 0 ? (
            <div className="dpEmpty">Nenhum empregador ativo. Cadastre o empregador em Cadastros.</div>
          ) : loading && !data ? (
            <div className="dpEmpty">Carregando...</div>
          ) : data && totals && previous ? (
            <>
              <div className="dpCounters">
                <div className="dpCounter">
                  <div className="dpCounter__value">{formatCurrency(totals.totalAmount)}</div>
                  <div className="dpCounter__label">Total provisionado ({totals.count} colaboradores)</div>
                </div>
                <div className="dpCounter">
                  <div className="dpCounter__value">{formatCurrency(totals.thirteenthAmount)}</div>
                  <div className="dpCounter__label">13º salário</div>
                </div>
                <div className="dpCounter">
                  <div className="dpCounter__value">{formatCurrency(Number(totals.vacationAmount) + Number(totals.vacationThirdAmount))}</div>
                  <div className="dpCounter__label">Férias + 1/3</div>
                </div>
                <div className="dpCounter">
                  <div className="dpCounter__value">{formatCurrency(totals.fgtsAmount)}</div>
                  <div className="dpCounter__label">FGTS</div>
                </div>
                <div className="dpCounter">
                  <div className="dpCounter__value">
                    {previous.totalAmount === null ? "—" : <Variation current={totals.totalAmount} previous={previous.totalAmount} />}
                  </div>
                  <div className="dpCounter__label">
                    {previous.totalAmount === null
                      ? `Sem foto de ${formatCompetence(previous.year, previous.month)}`
                      : `Variação contra a foto de ${formatCompetence(previous.year, previous.month)}`}
                  </div>
                </div>
                <div
                  className="dpCounter"
                  title={
                    `13º ${formatCurrency(data.nextMonth.thirteenthAmount)} · férias ${formatCurrency(data.nextMonth.vacationAmount)}` +
                    ` · 1/3 ${formatCurrency(data.nextMonth.vacationThirdAmount)} · FGTS ${formatCurrency(data.nextMonth.fgtsAmount)}` +
                    (data.nextMonth.excludedCount
                      ? `. ${data.nextMonth.excludedCount} fora (pendência de cadastro ou desligamento no mês).`
                      : "")
                  }
                >
                  <div className="dpCounter__value">{formatCurrency(data.nextMonth.totalAmount)}</div>
                  <div className="dpCounter__label">
                    Sugestão para provisionar em {formatCompetence(data.nextMonth.year, data.nextMonth.month)} ({data.nextMonth.count}{" "}
                    colaboradores)
                  </div>
                  <div className="dpFormHint">
                    Vai para a DRE de {formatCompetence(data.nextMonth.year, data.nextMonth.month)} como despesa quando a folha de{" "}
                    {data.nextMonth.month === 1
                      ? formatCompetence(data.nextMonth.year - 1, 12)
                      : formatCompetence(data.nextMonth.year, data.nextMonth.month - 1)}{" "}
                    fechar.
                  </div>
                </div>
              </div>

              {totals.pendingCount > 0 ? (
                <p className="dpNotice dpNotice--warning">
                  {totals.pendingCount} colaborador(es) com pendência de cadastro: a provisão deles pode estar incompleta.
                </p>
              ) : null}

              <section className="dpSection">
                <h2 className="dpSection__title">Por centro de custo</h2>
                <div className="dpTableWrapper">
                  <table className="dpTable">
                    <thead>
                      <tr>
                        <th>Centro de custo</th>
                        <th>Colaboradores</th>
                        <th>13º</th>
                        <th>Férias</th>
                        <th>1/3</th>
                        <th>FGTS</th>
                        <th>Total</th>
                        <th>Variação</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.byCostCenter.map((c) => (
                        <tr key={c.costCenterId ?? "-"}>
                          <td>{c.code ? `${c.code} · ${c.name}` : c.name}</td>
                          <td>{c.count}</td>
                          <td>{formatCurrency(c.thirteenthAmount)}</td>
                          <td>{formatCurrency(c.vacationAmount)}</td>
                          <td>{formatCurrency(c.vacationThirdAmount)}</td>
                          <td>{formatCurrency(c.fgtsAmount)}</td>
                          <td>
                            <strong>{formatCurrency(c.totalAmount)}</strong>
                          </td>
                          <td>
                            <Variation current={c.totalAmount} previous={c.previousTotal} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>

              <section className="dpSection">
                <h2 className="dpSection__title">Por colaborador</h2>
                <div className="dpControls">
                  <input
                    className="dpInput"
                    placeholder="Buscar por nome, CPF ou matrícula"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    style={{ maxWidth: 320 }}
                  />
                  <select className="dpSelect" aria-label="Ordenar" value={sort} onChange={(e) => setSort(e.target.value as SortKey)}>
                    <option value="name">Ordenar por nome</option>
                    <option value="total">Ordenar pelo maior total</option>
                  </select>
                </div>
                {rows.length === 0 ? (
                  <div className="dpEmpty">Nenhum colaborador ativo na data com esses filtros.</div>
                ) : (
                  <div className="dpTableWrapper">
                    <table className="dpTable">
                      <thead>
                        <tr>
                          <th>Colaborador</th>
                          <th>Centro de custo · loja</th>
                          <th>Avos 13º</th>
                          <th>13º</th>
                          <th>Dias de férias</th>
                          <th>Férias</th>
                          <th>1/3</th>
                          <th>FGTS</th>
                          <th>Total</th>
                          <th>Variação</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((r) => (
                          <tr key={r.contractId} onClick={() => openRow(r)} style={{ cursor: "pointer" }} title="Abrir a provisão na ficha">
                            <td>
                              <strong>{r.meta.fullName}</strong>
                              <div className="dpFormHint">
                                {r.meta.registrationNumber ? `Matrícula ${r.meta.registrationNumber} · ` : ""}
                                {formatCpf(r.meta.cpf)}
                              </div>
                              {r.pending.length ? (
                                <span className="dpBadge dpBadge--orange" title={r.pending.join(" ")}>
                                  Pendente
                                </span>
                              ) : null}
                              {r.warnings.length ? (
                                <span className="dpBadge dpBadge--gray" title={r.warnings.join(" ")}>
                                  Aviso
                                </span>
                              ) : null}
                            </td>
                            <td>
                              {r.meta.costCenter ? `${r.meta.costCenter.code} · ${r.meta.costCenter.name}` : "—"}
                              {r.meta.store ? <div className="dpFormHint">{r.meta.store}</div> : null}
                            </td>
                            <td>{r.thirteenthAvos}/12</td>
                            <td>{formatCurrency(r.thirteenthAmount)}</td>
                            <td>
                              {r.vacationDays.overdue ? (
                                <div>
                                  {days(r.vacationDays.overdue)} vencidos
                                  {r.vacationDays.doubled ? <span className="dpBadge dpBadge--red"> {days(r.vacationDays.doubled)} em dobro</span> : null}
                                </div>
                              ) : null}
                              <div className="dpFormHint">{days(r.vacationDays.proportional)} proporcionais</div>
                            </td>
                            <td>{formatCurrency(r.vacationAmount)}</td>
                            <td>{formatCurrency(r.vacationThirdAmount)}</td>
                            <td>{formatCurrency(r.fgtsAmount)}</td>
                            <td>
                              <strong>{formatCurrency(r.totalAmount)}</strong>
                            </td>
                            <td>
                              <Variation current={r.totalAmount} previous={r.previousTotal} />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
            </>
          ) : null}

          <section className="dpSection">
            <div className="dpControls" style={{ justifyContent: "space-between" }}>
              <h2 className="dpSection__title" style={{ margin: 0 }}>
                Fotos mensais
              </h2>
              <div className="dpControls" style={{ margin: 0 }}>
                <button type="button" className="dpBtn dpBtn--secondary" onClick={() => setShowHistory((v) => !v)} aria-expanded={showHistory}>
                  {showHistory ? "Ocultar fotos" : "Mostrar fotos"}
                </button>
                <select
                  className="dpSelect"
                  aria-label="Competência fechada"
                  value={snapshotChoice}
                  onChange={(e) => setSnapshotChoice(e.target.value)}
                  disabled={closedPeriods.length === 0}
                >
                  {closedPeriods.length === 0 ? <option value="">Nenhuma folha mensal fechada</option> : null}
                  {closedPeriods.map((p) => (
                    <option key={p.id} value={`${p.year}-${p.month}`}>
                      {formatCompetence(p.year, p.month)}
                      {snapshotMonths.has(`${p.year}-${p.month}`) ? "" : " (sem foto)"}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  className="dpBtn dpBtn--primary"
                  disabled={!snapshotChoice}
                  onClick={() => {
                    const [y, m] = snapshotChoice.split("-").map(Number);
                    setSnapshot({ year: y, month: m });
                  }}
                >
                  Gerar foto
                </button>
              </div>
            </div>
            {closedWithoutSnapshot.length > 0 ? (
              <p className="dpNotice dpNotice--warning">
                {closedWithoutSnapshot.length === 1
                  ? `A folha de ${formatCompetence(closedWithoutSnapshot[0].year, closedWithoutSnapshot[0].month)} está fechada sem foto das provisões.`
                  : `${closedWithoutSnapshot.length} folhas mensais fechadas estão sem foto das provisões.`}{" "}
                Use "Gerar foto".
              </p>
            ) : null}
            {showHistory ? (
              !history || history.months.length === 0 ? (
                <div className="dpEmpty">Nenhuma foto nos últimos 12 meses. Ela é gravada ao fechar a folha mensal.</div>
              ) : (
                <div className="dpTableWrapper">
                  <table className="dpTable">
                    <thead>
                      <tr>
                        <th>Competência</th>
                        <th>Colaboradores</th>
                        <th>13º</th>
                        <th>Férias</th>
                        <th>1/3</th>
                        <th>FGTS</th>
                        <th>Total</th>
                        <th>Variação</th>
                      </tr>
                    </thead>
                    <tbody>
                      {history.months.map((m, i) => {
                        const before = history.months[i + 1];
                        const consecutive = before && before.year * 12 + before.month === m.year * 12 + m.month - 1;
                        return (
                          <tr key={`${m.year}-${m.month}`}>
                            <td title={m.byCostCenter.map((c) => `${c.code ?? "—"} ${c.name}: ${formatCurrency(c.totalAmount)}`).join("\n")}>
                              {formatCompetence(m.year, m.month)}
                              {m.pendingCount ? <div className="dpFormHint">{m.pendingCount} com pendência</div> : null}
                            </td>
                            <td>{m.count}</td>
                            <td>{formatCurrency(m.thirteenthAmount)}</td>
                            <td>{formatCurrency(m.vacationAmount)}</td>
                            <td>{formatCurrency(m.vacationThirdAmount)}</td>
                            <td>{formatCurrency(m.fgtsAmount)}</td>
                            <td>
                              <strong>{formatCurrency(m.totalAmount)}</strong>
                            </td>
                            <td>{consecutive ? <Variation current={m.totalAmount} previous={before.totalAmount} /> : <span className="dpMuted">—</span>}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )
            ) : null}
          </section>
        </div>
      </main>

      <EcoConfirmDialog
        open={!!snapshot}
        title="Gerar a foto das provisões"
        message={
          snapshot ? (
            <>
              Calcula a provisão de cada colaborador ativo no último dia de {formatCompetence(snapshot.year, snapshot.month)} e grava a foto
              {snapshotMonths.has(`${snapshot.year}-${snapshot.month}`) ? ", substituindo a que existe" : ""}.
            </>
          ) : (
            ""
          )
        }
        confirmLabel="Gerar foto"
        busy={snapshotting}
        onConfirm={generateSnapshot}
        onCancel={() => setSnapshot(null)}
      />
    </div>
  );
}
